import { useState } from 'react';
import axios from 'axios';
import { Sparkle, X, Warning, Users, Clock, Flag, Wrench } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import useIA from '../../hooks/useIA';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DUREE_OPTIONS = [
  { v: 0, label: 'L\'IA décide selon les travaux' },
  { v: 3, label: '3 mois' },
  { v: 6, label: '6 mois' },
  { v: 9, label: '9 mois' },
  { v: 12, label: '12 mois' },
  { v: 18, label: '18 mois' },
  { v: 24, label: '24 mois' },
];

export default function GeneratePlanningIADialog({ chantierId, onClose, onImported }) {
  const [dateDebut, setDateDebut] = useState('');
  const [dureeMois, setDureeMois] = useState('0');
  const [contraintes, setContraintes] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [resetGantt, setResetGantt] = useState(false);
  const [importing, setImporting] = useState(false);
  // RP96 — réglage IA du cabinet : génération grisée avec explication si non disponible
  const ia = useIA();
  const iaOff = !ia.disponible('planning');

  const generate = async () => {
    if (!dateDebut) return toast.error('Date de début requise');
    try {
      setLoading(true);
      const res = await axios.post(
        `${API_URL}/api/chantiers/${chantierId}/planning/generer-ia`,
        { date_debut: dateDebut, duree_souhaitee_mois: Number(dureeMois) || 0, contraintes },
        { withCredentials: true, timeout: 120000 },
      );
      setResult(res.data);
      toast.success('Planning généré');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur IA. Réessayez ou ajustez les contraintes.');
    } finally {
      setLoading(false);
    }
  };

  const importPlanning = async () => {
    try {
      setImporting(true);
      const res = await axios.post(
        `${API_URL}/api/chantiers/${chantierId}/planning/importer-ia`,
        { reset_gantt: resetGantt },
        { withCredentials: true },
      );
      toast.success(res.data.message);
      onImported?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur import');
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !loading && !importing && onClose()}>
      <DialogContent className="rounded-none max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="generate-planning-ia-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'DM Serif Display, serif' }}>
            <Sparkle size={20} weight="fill" className="text-[#C4773B]" />
            Générer le planning par IA
          </DialogTitle>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4">
            <div className="bg-[#FFF5EB] border-l-4 border-[#C4773B] p-3 text-xs text-[#6B4A25]">
              L'IA analyse votre DPGF (lots, postes, quantités) et crée un planning réaliste avec l'ordre logique des interventions, les rendements métier et les durées justifiées.
            </div>

            <div>
              <Label className="text-xs">Date de démarrage du chantier *</Label>
              <Input
                type="date" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)}
                className="rounded-none mt-1" required data-testid="input-date-debut"
              />
            </div>

            <div>
              <Label className="text-xs">Durée souhaitée (optionnel)</Label>
              <Select value={dureeMois} onValueChange={setDureeMois}>
                <SelectTrigger className="rounded-none mt-1" data-testid="select-duree"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DUREE_OPTIONS.map((o) => <SelectItem key={o.v} value={String(o.v)}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs">Contraintes particulières (optionnel)</Label>
              <Textarea
                value={contraintes} onChange={(e) => setContraintes(e.target.value)}
                rows={4} className="rounded-none mt-1"
                placeholder="Ex : Travaux uniquement du lundi au vendredi. Accès limité en façade. Le lot toiture doit être terminé avant l'hiver. L'entreprise plomberie ne peut intervenir qu'à partir de juin..."
                data-testid="input-contraintes"
              />
            </div>

            {iaOff && (
              <p className="text-xs text-[#6B6B6B] bg-[#F5F5F3] px-3 py-2" data-testid="planning-ia-off">{ia.raison('planning')}</p>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onClose} className="rounded-none" disabled={loading}>Annuler</Button>
              <Button
                onClick={generate} disabled={!dateDebut || loading || iaOff}
                title={ia.raison('planning')}
                className="bg-[#C4773B] hover:bg-[#A85F25] rounded-none gap-2"
                data-testid="generate-ia-btn"
              >
                <Sparkle size={14} weight="fill" />
                {loading ? 'Génération en cours... (30-60s)' : 'Générer le planning'}
              </Button>
            </div>
          </div>
        ) : (
          <ResultView
            result={result}
            resetGantt={resetGantt} setResetGantt={setResetGantt}
            onRegenerate={() => setResult(null)}
            onImport={importPlanning}
            importing={importing}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function ResultView({ result, resetGantt, setResetGantt, onRegenerate, onImport, importing }) {
  return (
    <div className="space-y-4">
      {/* Résumé */}
      <div className="bg-[#F8F7F4] border-l-4 border-[#1E3A5F] p-4">
        <p className="text-[#1E3A5F] font-medium" style={{ fontFamily: 'DM Serif Display, serif' }}>{result.resume}</p>
        <div className="flex flex-wrap gap-4 mt-2 text-xs text-[#6B6B6B]">
          <span className="flex items-center gap-1"><Clock size={12} /> {result.duree_totale_semaines} semaines</span>
          <span className="flex items-center gap-1"><Flag size={12} /> Fin prévue : <strong className="text-[#2C2C2C]">{result.date_fin_previsionnelle}</strong></span>
          <span>{result.taches?.length || 0} tâches générées</span>
        </div>
      </div>

      {/* Alertes */}
      {result.alertes?.length > 0 && (
        <div className="bg-[#FFF5EB] border-l-4 border-[#C4773B] p-3">
          <h4 className="text-xs font-bold uppercase tracking-wider text-[#C4773B] mb-1 flex items-center gap-1">
            <Warning size={14} weight="fill" /> Alertes
          </h4>
          <ul className="text-xs text-[#6B4A25] space-y-0.5">
            {result.alertes.map((a, i) => <li key={i}>• {a}</li>)}
          </ul>
        </div>
      )}

      {/* Phases critiques */}
      {result.phases_critiques?.length > 0 && (
        <div className="bg-[#FFEBEE] border-l-4 border-[#8B3A3A] p-3">
          <h4 className="text-xs font-bold uppercase tracking-wider text-[#8B3A3A] mb-1 flex items-center gap-1">
            <Flag size={14} weight="fill" /> Phases critiques
          </h4>
          <ul className="text-xs text-[#8B3A3A] space-y-0.5">
            {result.phases_critiques.map((p, i) => <li key={i}>• {p}</li>)}
          </ul>
        </div>
      )}

      {/* Tableau tâches */}
      <div className="border border-[#E0E0DB] overflow-x-auto max-h-[400px] overflow-y-auto">
        <table className="w-full text-xs" data-testid="result-taches-table">
          <thead className="bg-[#1E3A5F] text-white sticky top-0 z-10">
            <tr>
              <th className="p-2 text-left">Tâche</th>
              <th className="p-2">Lot</th>
              <th className="p-2 text-left">Entreprise</th>
              <th className="p-2">Début</th>
              <th className="p-2">Durée</th>
              <th className="p-2">Ouvriers</th>
              <th className="p-2 text-left">Justification</th>
            </tr>
          </thead>
          <tbody>
            {(result.taches || []).map((t, i) => (
              <tr key={i} className={`border-t border-[#F0F0EB] ${t.phase_critique ? 'bg-[#FFF5F5]' : 'hover:bg-[#FAFAF8]'}`}>
                <td className="p-2 font-medium text-[#2C2C2C]">
                  {t.phase_critique && <Flag size={10} weight="fill" className="inline text-[#8B3A3A] mr-1" />}
                  {t.nom}
                </td>
                <td className="p-2 text-center text-[10px] text-[#6B6B6B]">{t.lot_code}</td>
                <td className="p-2 text-[10px] text-[#6B6B6B]">{t.entreprise || '—'}</td>
                <td className="p-2 text-center tabular-nums">{t.date_debut}</td>
                <td className="p-2 text-center font-semibold tabular-nums">{t.duree_jours}j</td>
                <td className="p-2 text-center tabular-nums">
                  <span className="inline-flex items-center gap-1"><Users size={10} />{t.nb_ouvriers}</span>
                </td>
                <td className="p-2 text-[10px] text-[#6B6B6B] max-w-xs">{t.notes}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Hypothèses */}
      {result.hypotheses?.length > 0 && (
        <details className="text-xs text-[#6B6B6B]">
          <summary className="cursor-pointer font-semibold">Hypothèses de calcul</summary>
          <ul className="mt-1 space-y-0.5 pl-4">
            {result.hypotheses.map((h, i) => <li key={i}>• {h}</li>)}
          </ul>
        </details>
      )}

      {/* Footer actions */}
      <div className="flex items-center justify-between pt-2 border-t border-[#E0E0DB]">
        <label className="flex items-center gap-2 text-xs text-[#6B6B6B] cursor-pointer">
          <input
            type="checkbox" checked={resetGantt} onChange={(e) => setResetGantt(e.target.checked)}
            className="accent-[#C4773B]" data-testid="reset-gantt-checkbox"
          />
          Remplacer les tâches existantes (sinon ajouter en plus)
        </label>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onRegenerate} className="rounded-none" disabled={importing} data-testid="regenerate-btn">
            Régénérer
          </Button>
          <Button onClick={onImport} disabled={importing} className="bg-[#2D6A4F] hover:bg-[#1E4A35] text-white rounded-none gap-2" data-testid="import-planning-ia-btn">
            <Wrench size={14} />
            {importing ? 'Import...' : `Importer dans le planning (${result.taches?.length || 0})`}
          </Button>
        </div>
      </div>
    </div>
  );
}
